const DATE_KEY = /^\d{4}-\d{2}-\d{2}$/

const isRecursable = (value: unknown): value is Record<string, unknown> | unknown[] =>
  value !== null
  && typeof value === 'object'
  && !(value instanceof Date)
  && !(value instanceof RegExp)
  && !(value instanceof Error)

function keyExcluded (key: string, exclude: Array<string | RegExp> | undefined): boolean {
  if (!exclude?.length) return false
  for (const pattern of exclude) {
    if (typeof pattern === 'string') {
      if (pattern === key) return true
    } else {
      pattern.lastIndex = 0
      if (pattern.test(key)) return true
    }
  }
  return false
}

/**
 * Same behavior as the `decamelize` package (underscore-style keys).
 */
function decamelize (str: string, separator: string = '_'): string {
  if (typeof str !== 'string') {
    throw new TypeError('Expected a string key')
  }
  return str
    .replace(/([a-z\d])([A-Z])/g, `$1${separator}$2`)
    .replace(/([A-Z]+)([A-Z][a-z\d]+)/g, `$1${separator}$2`)
    .toLowerCase()
}

function decamelizeInner (input: unknown, options: { deep: boolean, exclude?: Array<string | RegExp> }): unknown {
  if (Array.isArray(input)) {
    return input.map((item) => decamelizeInner(item, options))
  }
  if (!isRecursable(input)) {
    return input
  }
  const out: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(input as Record<string, unknown>)) {
    let v: unknown = value
    if (options.deep && isRecursable(value)) {
      v = decamelizeInner(value, options)
    }
    const newKey = keyExcluded(key, options.exclude) ? key : decamelize(key, '_')
    out[newKey] = v
  }
  return out
}

/** Deep camelCase → `snake_case` keys, aligned with {@link camelizeKeys} (same `DATE_KEY` rule). */
export const decamelizeKeys = <T extends Record<string, unknown> | ReadonlyArray<Record<string, unknown>>>(val: T) =>
  decamelizeInner(val, { deep: true, exclude: [DATE_KEY] }) as T
